import {
  AfterContentInit,
  Component,
  ContentChildren,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DropdownItemDirective } from '@app/shared/directives/dropdown-item.directive';
import { TippyDirective } from '@app/shared/directives/tippy.directive';
import { TranscludeDirective } from '@app/shared/directives/transclude.directive';
import { NgbDropdownItem } from '@ng-bootstrap/ng-bootstrap';
import { startWith } from 'rxjs/operators';
import { Placement as TippyPlacement } from 'tippy.js';

@Component({
  selector: 'portal-dropdown-menu',
  templateUrl: './dropdown-menu.component.html',
  styles: [],
  standalone: false,
})
export class DropdownMenuComponent implements AfterContentInit, OnChanges, OnDestroy, OnInit {
  @ContentChildren(NgbDropdownItem)
  dropdownItems!: QueryList<NgbDropdownItem>;

  @ContentChildren(TranscludeDirective, { descendants: true })
  transclusions!: QueryList<TranscludeDirective>;

  @ViewChild('tippy', { read: TippyDirective, static: false })
  tippy!: TippyDirective;

  @Input()
  appendTo: 'body' | 'parent' | Element | ((ref: Element) => Element) = 'body';

  @Input()
  arrow: boolean = false;

  @Input()
  buttonClass: string = '';

  @Input()
  buttonToggle: boolean = false;

  @Input()
  delay: number | [number | null, number | null] = 0;

  @Input()
  disabled: boolean = false;

  @Input()
  distance: number = 5;

  @Input()
  dropdownClass: string | null = null;

  @Input()
  horizontal: boolean = false;

  @Input()
  icon: string | null = null;

  @Input()
  set placement(value: string) {
    this._placement = value.replace('left', 'start').replace('right', 'end') as TippyPlacement;
  }

  @Input()
  preventAndStop: boolean = false;

  @Input()
  theme?: string;

  @Input()
  tooltip: string = 'general.moreActions';

  @Output()
  hidden = new EventEmitter<void>();

  @Output()
  shown = new EventEmitter<void>();

  _icon!: string;
  _placement: TippyPlacement = 'bottom-end';

  private destroyRef = inject(DestroyRef);

  closePopover = () => {
    this.tippy.hide();
    this.hidden.emit();
  };

  ngAfterContentInit() {
    this.dropdownItems.changes
      .pipe(startWith(this.dropdownItems), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.removeChildListeners(this.dropdownItems);
        this.addChildListeners(this.dropdownItems);
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    const horizontal = changes.horizontal?.currentValue ?? this.horizontal;
    const icon = changes.icon?.currentValue ?? this.icon;

    this.updateIcon(horizontal, icon);
  }

  ngOnDestroy() {
    this.removeChildListeners(this.dropdownItems);
  }

  ngOnInit() {
    this.updateIcon(this.horizontal, this.icon);
  }

  onClick($event: MouseEvent) {
    if (this.preventAndStop !== false) {
      $event.preventDefault();
      $event.stopPropagation();
    }
  }

  private addChildListeners(items: QueryList<NgbDropdownItem | DropdownItemDirective>): void {
    items?.forEach((item) => {
      item.nativeElement.addEventListener('click', this.closePopover);
    });
  }

  private removeChildListeners(items: QueryList<NgbDropdownItem | DropdownItemDirective>): void {
    items?.forEach((child) => {
      child.nativeElement.addEventListener('click', this.closePopover);
    });
  }

  private updateIcon(horizontal: boolean, icon: string): void {
    if (icon != null) {
      this._icon = icon;
    } else if (horizontal === true) {
      this._icon = 'far fa-fw fa-ellipsis-h';
    } else if (horizontal === false) {
      this._icon = 'far fa-fw fa-ellipsis-v';
    }
  }
}
